import styles from "../../../styles/components/Modal/Employees/EditUserModal.module.css";
import React, { useEffect, useState, useCallback, useRef, useContext } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { SideBar } from "./components/SideBar";
import { PersonalData } from "./components/PersonalData";
import { PermissionGroupTab } from "./components/NewUser/PermissionGroupTab";

import { getAdminById } from "../../../utils/api/Admin/getAdminById";
import { updateAdmin } from "../../../utils/api/Admin/updateAdminApi";
import { getPermissions } from '../../../utils/api/Admin/Permissions/getPermissionsApi';
import { syncAdminPermissions } from "../../../utils/api/Admin/Permissions/syncAdminPermissionsApi";
import { getRoles } from '../../../utils/api/Admin/getRoles';
import { getAllDesksApi } from '../../../utils/api/Admin/desk/getAllDesksApi';
import { permissionTranslations } from '../../../utils/translations/permissionTranslations';
import { handleError } from "../../../utils/custom/errorHandler";
import { LangContext } from "../../../context/LangContext";
import { ThemeContext } from "../../../context/ThemeContext";
import { MultiSelectDropdown } from '../../UI/MultiSelectDropdown';
import { DropdownSelector } from '../../UI/DropdownSelector';
import { CommonPermissionGroupsTab } from './components/NewUser/CommonPermissionGroupsTab';
import { COMMON_PERMISSIONS_TAB_KEY, getAvailableCommonPermissionGroups } from '../../../utils/commonPermissionGroups';
import { isTelegramTagValid } from '../../../utils/validation/telegramTag';
import { LeadFieldAccessTab } from './components/NewUser/LeadFieldAccessTab';
import {
    createDefaultLeadFieldAccessModes,
    LEAD_FIELD_ACCESS_TAB_KEY,
    getLeadFieldAccessSections,
    normalizeLeadFieldAccessModes,
} from '../../../utils/leadFieldAccess';

const translations = {
    ru: {
        loadingData: "Загрузка данных...",
        loadingMessage: "Данные загружаются, пожалуйста подождите...",
        editUser: "Изменить сотрудника",
        personalData: "Персональные данные",
        commonPermissions: "Общие",
        leadFieldAccess: "Поля лида",
        generalError: "Ошибка при загрузке всех данных:",
        updating: "Обновление...",
        saveChanges: "Сохранить изменения",
        invalidTelegramTag: "Telegram должен быть в формате @user_tag",
    },
    en: {
        loadingData: "Loading data...",
        loadingMessage: "Data is loading, please wait...",
        editUser: "Edit Employee",
        personalData: "Personal Data",
        commonPermissions: "Common",
        leadFieldAccess: "Lead Fields",
        generalError: "Error loading all data:",
        updating: "Updating...",
        saveChanges: "Save changes",
        invalidTelegramTag: "Telegram must match the format @user_tag",
    },
};

export const EditUserModal = ({ id, handleEditUserModal, onUserUpdated, permissions }) => {
    const { langSelected } = useContext(LangContext);
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';
    const t = translations[langSelected] || translations.ru;
    const pT = permissionTranslations;

    const [activeTab, setActiveTab] = useState('personalData');
    const [adminInfo, setAdminInfo] = useState({
        first_name: '',
        last_name: '',
        email: '',
        telegram_tag: '',
        telephony_extension: '',
        role: null,
        desk_ids: [],
        permissions: [],
        lead_field_access: createDefaultLeadFieldAccessModes(),
        status: '',
        password: '',
    });
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const modalRef = useRef(null);

    const [rolesData, setRolesData] = useState([]);
    const [desksData, setDesksData] = useState([]);
    const [roleOptionsForDropdown, setRoleOptionsForDropdown] = useState([]);
    const [deskOptionsForDropdown, setDeskOptionsForDropdown] = useState([]);

    const [allPermissions, setAllPermissions] = useState([]);
    const [groupedPermissions, setGroupedPermissions] = useState({});
    const [commonPermissionGroups, setCommonPermissionGroups] = useState([]);
    const [leadFieldAccessSections, setLeadFieldAccessSections] = useState([]);
    const [permissionGroupDisplayNames, setPermissionGroupDisplayNames] = useState({});
    const [availableTabs, setAvailableTabs] = useState(['personalData']);
    const canManageAdminPermissions = Boolean(permissions['user.change_admin_permissions']);

    const handleClickOutside = useCallback((event) => {
        if (modalRef.current && !modalRef.current.contains(event.target)) {
            handleEditUserModal();
        }
    }, [handleEditUserModal]);

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [handleClickOutside]);

    const clearValidationError = useCallback((field) => {
        setValidationErrors(prev => {
            const newErrors = { ...prev };
            if (newErrors[field]) {
                delete newErrors[field];
            }
            return newErrors;
        });
    }, []);

    useEffect(() => {
        const fetchAllData = async () => {
            setIsLoading(true);
            setValidationErrors({});

            try {
                const [roles, desks, permissionsResponse, adminResult] = await Promise.all([
                    getRoles(),
                    getAllDesksApi(1),
                    getPermissions(),
                    getAdminById(id)
                ]);

                setRolesData(roles.data);
                setRoleOptionsForDropdown(roles.data.map(role => ({ value: role.id, label: role.name })));

                setDesksData(desks.data);
                setDeskOptionsForDropdown(desks.data.map(desk => ({ value: desk.id, label: desk.name, description: desk.description })));

                setAllPermissions(permissionsResponse.data);
                const availableCommonGroups = getAvailableCommonPermissionGroups(permissionsResponse.data);
                setCommonPermissionGroups(availableCommonGroups);
                setLeadFieldAccessSections(getLeadFieldAccessSections(langSelected));
                const permissionsByGroup = permissionsResponse.data.reduce((acc, permission) => {
                    const groupName = permission.group || "Без группы";
                    if (!acc[groupName]) {
                        acc[groupName] = [];
                    }
                    acc[groupName].push(permission);
                    return acc;
                }, {});
                setGroupedPermissions(permissionsByGroup);

                const groupDisplayNames = {
                    personalData: t.personalData,
                    [COMMON_PERMISSIONS_TAB_KEY]: t.commonPermissions,
                    [LEAD_FIELD_ACCESS_TAB_KEY]: t.leadFieldAccess,
                };
                Object.keys(permissionsByGroup).forEach(group => {
                    groupDisplayNames[group] = pT.groups[group]?.[langSelected] || permissionsByGroup[group][0]?.group_display_name || group;
                });
                setPermissionGroupDisplayNames(groupDisplayNames);

                const dynamicTabs = Object.keys(permissionsByGroup).sort((a, b) => {
                    const nameA = pT.groups[a]?.[langSelected] || a;
                    const nameB = pT.groups[b]?.[langSelected] || b;
                    return nameA.localeCompare(nameB);
                });
                setAvailableTabs([
                    'personalData',
                    ...(availableCommonGroups.length > 0 ? [COMMON_PERMISSIONS_TAB_KEY] : []),
                    ...(canManageAdminPermissions ? [LEAD_FIELD_ACCESS_TAB_KEY] : []),
                    ...dynamicTabs
                ]);

                const extractedPermissionNames = Array.isArray(adminResult.permissions)
                    ? adminResult.permissions.map(p => p.name)
                    : [];

                const extractedDeskIds = Array.isArray(adminResult.desks)
                    ? adminResult.desks.map(d => d.id)
                    : [];

                setAdminInfo({
                    first_name: adminResult.first_name || '',
                    last_name: adminResult.last_name || '',
                    email: adminResult.email || '',
                    telegram_tag: adminResult.telegram_tag || '',
                    telephony_extension: adminResult.telephony_extension || '',
                    role: adminResult.role?.id || null,
                    desk_ids: extractedDeskIds,
                    permissions: extractedPermissionNames,
                    lead_field_access: normalizeLeadFieldAccessModes(adminResult.lead_field_access),
                    status: adminResult.status || '',
                    password: '',
                });

            } catch (err) {
                console.error(t.generalError, err.message);
                handleError(err.response?.data || err, setValidationErrors);
            } finally {
                setIsLoading(false);
            }
        };

        if (id) {
            fetchAllData();
        }

    }, [id, t.personalData, t.commonPermissions, t.leadFieldAccess, t.generalError, pT, langSelected, canManageAdminPermissions]);

    const handlePersonalDataChange = useCallback((data) => {
        setAdminInfo(prev => ({ ...prev, ...data }));
        if (data.first_name !== undefined) clearValidationError('first_name');
        if (data.last_name !== undefined) clearValidationError('last_name');
        if (data.email !== undefined) clearValidationError('email');
        if (data.telegram_tag !== undefined) clearValidationError('telegram_tag');
        if (data.telephony_extension !== undefined) clearValidationError('telephony_extension');
        if (data.password !== undefined) clearValidationError('password');
    }, [clearValidationError]);

    const handleRoleChange = useCallback((selectedRoleId) => {
        setAdminInfo(prev => ({ ...prev, role: selectedRoleId }));
        clearValidationError('role');
    }, [clearValidationError]);

    const handleDeskChange = useCallback((filterName, selectedDeskIdsArray, filterKey) => {
        setAdminInfo(prev => ({ ...prev, desk_ids: selectedDeskIdsArray }));
        clearValidationError('desk_ids');
    }, [clearValidationError]);

    const handlePermissionChange = useCallback((permissionName, isChecked) => {
        setAdminInfo(prevData => {
            const currentPermissions = prevData.permissions;
            let updatedPermissions;
            if (isChecked) {
                updatedPermissions = [...new Set([...currentPermissions, permissionName])];
            } else {
                updatedPermissions = currentPermissions.filter(name => name !== permissionName);
            }
            return { ...prevData, permissions: updatedPermissions };
        });
    }, []);

    const handlePermissionDropdownChange = useCallback((filterName, selectedValue, filterKey) => {
        setAdminInfo(prevData => {
            const currentPermissions = new Set(prevData.permissions);

            allPermissions.forEach(p => {
                if (p.name.startsWith(filterKey + '.')) {
                    currentPermissions.delete(p.name);
                }
            });

            if (selectedValue) {
                currentPermissions.add(selectedValue);
            }
            return { ...prevData, permissions: Array.from(currentPermissions) };
        });
    }, [allPermissions]);

    const handleCommonPermissionGroupToggle = useCallback((permissionNames, isEnabled) => {
        setAdminInfo((prevData) => {
            const nextPermissions = new Set(prevData.permissions);

            permissionNames.forEach((permissionName) => {
                if (isEnabled) {
                    nextPermissions.add(permissionName);
                } else {
                    nextPermissions.delete(permissionName);
                }
            });

            return {
                ...prevData,
                permissions: Array.from(nextPermissions),
            };
        });
    }, []);

    const handleLeadFieldAccessChange = useCallback((fieldKey, nextMode) => {
        setAdminInfo((prevData) => ({
            ...prevData,
            lead_field_access: {
                ...prevData.lead_field_access,
                [fieldKey]: nextMode,
            },
        }));
    }, []);

    const handleSaveChanges = async () => {
        setIsSubmitting(true);
        setValidationErrors({});

        if (!isTelegramTagValid(adminInfo.telegram_tag)) {
            setValidationErrors({ telegram_tag: t.invalidTelegramTag });
            setActiveTab('personalData');
            setIsSubmitting(false);
            return;
        }

        try {
            const dataToUpdate = {
                first_name: adminInfo.first_name,
                last_name: adminInfo.last_name,
                email: adminInfo.email,
                telegram_tag: adminInfo.telegram_tag,
                telephony_extension: adminInfo.telephony_extension,
                role: adminInfo.role,
                desk_ids: adminInfo.desk_ids,
            };
            
            if (adminInfo.password) {
                dataToUpdate.password = adminInfo.password;
            }

            await updateAdmin(id, dataToUpdate);
            await syncAdminPermissions(id, adminInfo.permissions, adminInfo.lead_field_access);

            setIsSubmitting(false);
            if (onUserUpdated) {
                onUserUpdated();
            }
        } catch (err) {
            console.error("Ошибка при обновлении администратора или синхронизации прав:", err);
            setIsSubmitting(false);

            handleError(err.response?.data || err, setValidationErrors);
        }
    };

    if (isLoading) {
        return (
            <div className={`${styles.modal} ${isDark ? styles.darkModal : ''}`}>
                <div className={`${styles.filtersContainer} ${isDark ? styles.darkFiltersContainer : ''}`} ref={modalRef}>
                    <div className={`${styles.modalLabel} ${isDark ? styles.darkModalLabel : ''}`}>
                        <p className={`${styles.modalLabelText} ${isDark ? styles.darkModalLabelText : ''}`}>{t.loadingData}</p>
                        <div className={`${styles.modalLabelCloseButton} ${isDark ? styles.darkModalLabelCloseButton : ''}`} onClick={handleEditUserModal}>
                            <CloseIcon />
                        </div>
                    </div>
                    <div className={`${styles.modalContent} ${isDark ? styles.darkModalContent : ''}`} style={{ justifyContent: 'center', alignItems: 'center' }}>
                        <p>{t.loadingMessage}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`${styles.modal} ${isDark ? styles.darkModal : ''}`}>
            <div className={`${styles.filtersContainer} ${isDark ? styles.darkFiltersContainer : ''}`} ref={modalRef}>
                <div className={`${styles.modalLabel} ${isDark ? styles.darkModalLabel : ''}`}>
                    <div className={styles.modalLabelTextContainer}>
                        <p className={`${styles.modalLabelText} ${isDark ? styles.darkModalLabelText : ''}`}>{t.editUser}</p>
                    </div>
                    <div className={styles.modalLabelCloseButtonContainer}>
                        <div className={`${styles.modalLabelCloseButton} ${isDark ? styles.darkModalLabelCloseButton : ''}`} onClick={handleEditUserModal}>
                            <CloseIcon />
                        </div>
                    </div>
                </div>
                <div className={styles.modalContent}>
                    <div className={`${styles.leftPanel} ${isDark ? styles.darkLeftPanel : ''}`}>
                        {
                            permissions['user.change_admin_permissions'] && (
                                <SideBar
                                    setActiveTab={setActiveTab}
                                    activeTab={activeTab}
                                    tabs={availableTabs}
                                    permissionGroupDisplayNames={permissionGroupDisplayNames}
                                    isDark={isDark}
                                />
                            )
                        }
                    </div>
                    <div className={`${styles.section} ${isDark ? styles.darkSection : ''}`}>
                        {validationErrors.general && (
                            <div className={`${styles.generalError} ${isDark ? styles.darkGeneralError : ''}`} >
                                {validationErrors.general}
                            </div>
                        )}

                        {activeTab === 'personalData' && (
                            <PersonalData
                                userInfo={adminInfo}
                                setUserInfo={handlePersonalDataChange}
                                roleOptions={roleOptionsForDropdown}
                                onRoleChange={handleRoleChange}
                                deskOptions={deskOptionsForDropdown}
                                onDeskChange={handleDeskChange}
                                validationErrors={validationErrors}
                                setValidationErrors={setValidationErrors}
                                selectedDeskIds={adminInfo.desk_ids}
                                DropdownComponent={DropdownSelector}
                                MultiSelectComponent={MultiSelectDropdown}
                                isDark={isDark}
                                showTelephonyExtension={true}
                            />
                        )}

                        {activeTab === COMMON_PERMISSIONS_TAB_KEY && permissions['user.change_admin_permissions'] && commonPermissionGroups.length > 0 && (
                            <CommonPermissionGroupsTab
                                groups={commonPermissionGroups}
                                selectedPermissions={adminInfo.permissions}
                                onToggleGroup={handleCommonPermissionGroupToggle}
                                groupedPermissions={groupedPermissions}
                                isDark={isDark}
                            />
                        )}

                        {activeTab === LEAD_FIELD_ACCESS_TAB_KEY && permissions['user.change_admin_permissions'] && (
                            <LeadFieldAccessTab
                                sections={leadFieldAccessSections}
                                leadFieldAccess={adminInfo.lead_field_access}
                                onLeadFieldAccessChange={handleLeadFieldAccessChange}
                            />
                        )}

                        {Object.keys(groupedPermissions).map(groupName => (
                            activeTab === groupName && permissions['user.change_admin_permissions'] && (
                                <PermissionGroupTab
                                    key={groupName}
                                    groupName={groupName}
                                    groupDisplayName={permissionGroupDisplayNames[groupName]}
                                    permissions={groupedPermissions[groupName]}
                                    selectedPermissions={adminInfo.permissions}
                                    handlePermissionChange={handlePermissionChange}
                                    handlePermissionDropdownChange={handlePermissionDropdownChange}
                                    isDark={isDark}
                                />
                            )
                        ))}
                    </div>
                </div>
                <div className={`${styles.modalFooter} ${isDark ? styles.darkModalFooter : ''}`}>
                    <div className={styles.confirmationWrapper}>
                        {isSubmitting ? (
                            <button className={`${styles.submitButton} ${isDark ? styles.darkSubmitButton : ''}`} disabled>{t.updating}</button>
                        ) : (
                            <button
                                className={`${styles.submitButton} ${isDark ? styles.darkSubmitButton : ''}`}
                                onClick={handleSaveChanges}
                                disabled={isSubmitting}
                            >
                                {t.saveChanges}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
